import * as PIXI from 'pixi.js';
import type { DisplayObjectEvents } from 'pixi.js';
import {
    configure,
    reaction as mobxReaction,
    type IReactionDisposer,
    type IReactionOptions,
    type IReactionPublic,
} from 'mobx';
export { autorun, comparer, computed, createAtom, observable, runInAction, untracked } from 'mobx';

// Luminary assigns observable properties from ordinary setters and load paths all over the codebase, which mobx's
// default `enforceActions: 'observed'` warns about once the property is observed — a single test file produces
// upwards of 60 warnings. This is warning suppression only: nothing depends on it for correctness, and the whole
// suite passes without it. Tighten this as classes move onto reactions and their writes become actions; until then
// mobx cannot flag a genuinely unsafe write, so reactions must not be assumed to be action-wrapped.
configure({ enforceActions: 'never' });

/**
 * mobx's `reaction` with `fireImmediately` defaulting to true, because a derivation that does not run until its
 * inputs next change leaves the object it derives unbuilt. Pass `{ fireImmediately: false }` to opt out.
 */
export function reaction<T, FireImmediately extends boolean = true>(
    expression: (r: IReactionPublic) => T,
    effect: (
        value: T,
        previousValue: FireImmediately extends true ? T | undefined : T,
        r: IReactionPublic,
    ) => void,
    opts?: IReactionOptions<T, FireImmediately>,
): IReactionDisposer {
    // the merged literal widens to boolean, which no longer matches the FireImmediately the caller pinned
    const options = { fireImmediately: true, ...opts } as IReactionOptions<T, FireImmediately>;
    return mobxReaction(expression, effect, options);
}

/**
 * Base class for objects that derive state reactively. Reactions registered through
 * [addReaction]{@link ReactiveBase#addReaction} are disposed when the object is destroyed.
 */
export class ReactiveBase extends PIXI.utils.EventEmitter<DisplayObjectEvents> {
    #disposers: IReactionDisposer[] = [];

    /**
     * Ties the lifetime of a mobx reaction to this object. Register from the constructor, after assigning every
     * property the reaction reads: `autorun` runs its callback immediately, so anything assigned on a later line is
     * not visible to that first run.
     */
    protected addReaction(disposer: IReactionDisposer): void {
        this.#disposers.push(disposer);
    }

    destroy(_args?: unknown): void {
        this.removeAllListeners();
        this.#disposers.forEach((dispose) => dispose());
        this.#disposers.length = 0;
    }
}
